import { useEffect } from "react";

/** Mark portal dialogs (My Zone / league game summaries) so wheel lock allows their inner scroll. */
export const OVERLAY_SCROLL_ROOT_SELECTOR = ".ReactModal__Content, [data-c8-overlay-scroll]";

/** Applied while a portal overlay is open. Independent of inline `overflow` used by drawers / play shells. */
export const OVERLAY_SCROLL_LOCK_CLASS = "c8-overlay-scroll-lock";

/**
 * Applied by Layout on full-viewport play shells (Sixes). Kept as a class so overlay lock/unlock
 * cannot wipe html/body overflow and leave the inner card scroller with nothing to pan.
 */
export const DOCUMENT_SCROLL_LOCK_CLASS = "c8-document-scroll-lock";

/** Nested page scrollers (e.g. league detail `overflow-y-auto`) keep scrolling when only body is locked. */
let openModalCount = 0;

function elementCanScroll(el: Element): boolean {
    const style = window.getComputedStyle(el);
    const oy = style.overflowY;
    const ox = style.overflowX;
    const canY =
        (oy === "auto" || oy === "scroll" || oy === "overlay") && el.scrollHeight > el.clientHeight + 1;
    const canX =
        (ox === "auto" || ox === "scroll" || ox === "overlay") && el.scrollWidth > el.clientWidth + 1;
    return canY || canX;
}

/** Allow touch/wheel only inside a scrollable region of an open overlay. */
function isScrollAllowedTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const modal = target.closest(OVERLAY_SCROLL_ROOT_SELECTOR);
    if (!modal) return false;
    let node: Element | null = target;
    while (node && node !== modal) {
        if (elementCanScroll(node)) return true;
        node = node.parentElement;
    }
    return elementCanScroll(modal);
}

function preventBackgroundScroll(event: Event) {
    if (isScrollAllowedTarget(event.target)) return;
    event.preventDefault();
}

function overlayLockIsApplied(): boolean {
    return document.documentElement.classList.contains(OVERLAY_SCROLL_LOCK_CLASS);
}

export function lockBackgroundScroll() {
    openModalCount += 1;
    if (openModalCount !== 1) return;
    document.documentElement.classList.add(OVERLAY_SCROLL_LOCK_CLASS);
    document.body.classList.add(OVERLAY_SCROLL_LOCK_CLASS);
    document.addEventListener("touchmove", preventBackgroundScroll, { passive: false, capture: true });
    document.addEventListener("wheel", preventBackgroundScroll, { passive: false, capture: true });
}

export function unlockBackgroundScroll() {
    if (openModalCount === 0) return;
    openModalCount -= 1;
    if (openModalCount !== 0) return;
    document.removeEventListener("touchmove", preventBackgroundScroll, { capture: true } as EventListenerOptions);
    document.removeEventListener("wheel", preventBackgroundScroll, { capture: true } as EventListenerOptions);
    document.documentElement.classList.remove(OVERLAY_SCROLL_LOCK_CLASS);
    document.body.classList.remove(OVERLAY_SCROLL_LOCK_CLASS);
}

/** Test helper — not used in app code. */
export function resetOverlayScrollLockForTests() {
    if (openModalCount > 0) {
        openModalCount = 1;
        unlockBackgroundScroll();
    }
    openModalCount = 0;
    if (typeof document === "undefined") return;
    document.documentElement.classList.remove(OVERLAY_SCROLL_LOCK_CLASS);
    document.body?.classList.remove(OVERLAY_SCROLL_LOCK_CLASS);
}

export function overlayScrollLockOpenCountForTests(): number {
    return openModalCount;
}

export function overlayScrollLockIsAppliedForTests(): boolean {
    return overlayLockIsApplied();
}

/** Lock page / nested league column scroll while a portal overlay is open. */
export function useOverlayScrollLock(active: boolean) {
    useEffect(() => {
        if (!active) return undefined;
        lockBackgroundScroll();
        return () => unlockBackgroundScroll();
    }, [active]);
}
